import { useState } from "react";

import { ApolloQueryResult } from "@apollo/client";
import { Button, Callout, Colors, Icon } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { compareDesc, differenceInMinutes } from "date-fns";
import max from "lodash/max";

import calendar from "@/../../hypetrain/static/img/program/calendar.svg";
import completed from "@/../../hypetrain/static/img/program/completed.svg";
import design from "@/../../hypetrain/static/img/program/design.svg";
import launch from "@/../../hypetrain/static/img/program/launch.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { DesignProgramPanel } from "@/components/pages_logged_in/program/components/DesignProgramPanel";
import { LaunchProgramPanel } from "@/components/pages_logged_in/program/components/LaunchProgramPanel";
import { MeetingScheduleButton } from "@/components/pages_logged_in/program/components/MeetingScheduleButton";
import { ProgramComponent } from "@/components/pages_logged_in/program/components/ProgramComponent";
import { ProgramDashboardDialogs } from "@/components/pages_logged_in/program/components/program-dashboard/ProgramDashboardDialogs";
import { DateTimeDisplay } from "@/components/pieces/DateTimeDisplay";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { BRAND_PURPLE, CARD_CLASSES, MIDTEXT_COLOR } from "@/css/constants";
import {
  ComponentStatus,
  ComponentTypeEnum,
  ProgramComponentsQuery,
  ProgramQuery,
  ProgramStatus,
  useCompleteAddToCalendarMutation,
} from "@/graphql";
import { cn } from "@/lib/utils";
import {
  ComponentDialog,
  isLeadComponent,
  LeadComponent,
  MainComponent,
  MainProgram,
  PanelComponent,
  ParticipantComponent,
  ProgramRole,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

import { getExpandBlock, getSortedComponents } from "./utils";

type ProgramComponentType =
  | LeadComponent
  | ParticipantComponent
  | MainComponent;

function Divider() {
  return <Box className="bbs" maxWidth={720} my={40} />;
}

const ComponentCountContainer = styled(Flex)`
  background-color: ${BRAND_PURPLE};
  color: ${Colors.WHITE};
  text-align: center;
  border-radius: 50%;
  border-radius: 50%;
  padding: 2px;
  font-size: 10px;
  min-width: 18px;
  min-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
`;

interface TitleBoxProps {
  title: string;
  componentCount?: number;
  collapsible?: boolean;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

function TitleBox({
  title,
  componentCount = 0,
  collapsible = false,
  isCollapsed = false,
  onToggle,
}: TitleBoxProps) {
  // Content to be rendered
  const content = (
    <Flex
      fontSize={18}
      fontWeight={600}
      mb={3}
      alignItems="center"
      style={{ display: "inline-flex" }}
    >
      <Box>{title}</Box>
      {componentCount > 0 && (
        <ComponentCountContainer>{componentCount}</ComponentCountContainer>
      )}

      {collapsible && (
        <Flex pl={2} height={27} alignItems="center">
          <Icon icon={isCollapsed ? "chevron-down" : "chevron-up"} />
        </Flex>
      )}
    </Flex>
  );

  return collapsible ? (
    <Clickable onClick={onToggle}>{content}</Clickable>
  ) : (
    content
  );
}

interface ComponentListProps {
  components: PanelComponent[];
  program: MainProgram;
  role: ProgramRole;
  title?: string;
  onEdit?: (component: PanelComponent) => void;
  onDelete?: (component: PanelComponent) => void;
  onClose?: (component: PanelComponent) => void;
  large?: boolean;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
}

export function ComponentList({
  components,
  program,
  role,
  title,
  onEdit,
  onDelete,
  onClose,
  large = false,
  collapsible = false,
  defaultCollapsed = false,
}: ComponentListProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  if (!components?.length) return null;

  const handleToggle = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <>
      {title && (
        <TitleBox
          title={title}
          collapsible={collapsible}
          isCollapsed={isCollapsed}
          onToggle={handleToggle}
        />
      )}
      {!isCollapsed &&
        components.map((component) => (
          <ProgramComponent
            key={component?.id}
            component={component}
            program={program}
            role={role}
            onEdit={() => onEdit?.(component)}
            onDelete={() => onDelete?.(component)}
            onClose={() => onClose?.(component)}
            large={large}
          />
        ))}
    </>
  );
}

export function AllSetForNow({ mb = 3 }) {
  return (
    <Box
      flex={1}
      mb={mb}
      className={cn(CARD_CLASSES, "xs:!p-3.5 md:!p-6")}
      css={{ background: Colors.WHITE }}
      maxWidth={720}
    >
      <Flex alignItems={"center"}>
        <EmbedSvg className="xs:size-10 md:size-12.5" src={completed} />
        <Flex
          className="xs:!ml-3.5 xs:gap-px flex-col md:!ml-4 md:gap-1.5"
          flex={1}
        >
          <Box className="xs:text-lg font-semibold md:text-xl">
            All set for now
          </Box>
          <Box className="xs:text-sm font-normal md:text-base">Nice work!</Box>
        </Flex>
      </Flex>
    </Box>
  );
}

interface LookingForwardSectionProps {
  isLead: boolean;
  isParticipant: boolean;
  isBothRoles: boolean;
  leadUpcoming: LeadComponent[];
  participantUpcoming: ParticipantComponent[];
  program: MainProgram;
  handleEditComponent: (component: PanelComponent) => void;
  handleDeleteComponent: (component: PanelComponent) => void;
  handleCloseComponent: (component: PanelComponent) => void;
  refetchProgramComponents: () => void;
}

function LookingForwardSection({
  isLead,
  isParticipant,
  isBothRoles,
  leadUpcoming,
  participantUpcoming,
  program,
  handleEditComponent,
  handleDeleteComponent,
  handleCloseComponent,
  refetchProgramComponents,
}: LookingForwardSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);

  if (
    !(isLead && leadUpcoming.length > 0) &&
    !(isParticipant && participantUpcoming.length > 0)
  ) {
    return null;
  }

  const displayParticipantUpcoming = participantUpcoming.filter(
    (item) =>
      (!!item.childComponents?.length &&
        item.type === ComponentTypeEnum.OneOneCoachingMaster) ||
      (!!item.meetings?.length &&
        [
          ComponentTypeEnum.OneOneCoachingIntro,
          ComponentTypeEnum.OneOneCoachingSession,
        ].includes(item.type)) ||
      ![
        ComponentTypeEnum.OneOneCoachingSession,
        ComponentTypeEnum.OneOneCoachingMaster,
      ].includes(item.type),
  );

  const totalUpcoming = isLead
    ? leadUpcoming?.length
    : displayParticipantUpcoming?.length;

  const shouldShowMeetingSection = program.status !== ProgramStatus.Live;

  return (
    <>
      <Divider />
      <TitleBox
        title="Looking Forward"
        componentCount={totalUpcoming}
        collapsible={true}
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(!isCollapsed)}
      />
      {!isCollapsed && (
        <>
          {isLead && leadUpcoming.length > 0 && (
            <>
              {shouldShowMeetingSection && (
                <MeetingSection
                  components={getGroupMeetingComponentForAdding(leadUpcoming)}
                  showDoneButton={false}
                  program={program}
                  refetchProgramComponents={refetchProgramComponents}
                />
              )}
              <ComponentList
                components={leadUpcoming}
                program={program}
                role="lead"
                onEdit={handleEditComponent}
                onDelete={handleDeleteComponent}
                onClose={handleCloseComponent}
              />
            </>
          )}
          {isBothRoles && displayParticipantUpcoming.length > 0 && (
            <Box mt={4}>
              <TitleBox title="Looking Forward (As Participant)" />
            </Box>
          )}
          {isParticipant && displayParticipantUpcoming.length > 0 && (
            <ComponentList
              components={displayParticipantUpcoming}
              program={program}
              role="participant"
            />
          )}
        </>
      )}
    </>
  );
}

interface MeetingSectionItemProps {
  component: LeadComponent;
  program: MainProgram;
}

function MeetingSectionItem({ component, program }: MeetingSectionItemProps) {
  const endDate = component.targetEndDate
    ? new Date(component.targetEndDate)
    : new Date();

  const startDate = component.targetStartDate
    ? new Date(component.targetStartDate)
    : new Date();

  const duration = differenceInMinutes(endDate, startDate);

  return (
    <Box
      alignItems="center"
      justifyContent="space-between"
      css={{
        border: "1px solid #E2E2E2",
        borderRadius: 8,
        padding: 24,
      }}
    >
      <Flex
        alignItems={"center"}
        flex={1}
        justifyContent={"space-between"}
        style={{ gap: 16 }}
      >
        <Box>
          <Box fontSize={16} fontWeight={500}>
            {component.name}
          </Box>
          <Box fontSize={14} color={MIDTEXT_COLOR} display={"flex"}>
            <DateTimeDisplay
              time={component.targetStartDate}
              formatString="MMM do 'at' h:mm a"
            />
            <>
              <Box>・</Box>
              <Box display={"inline-block"} ml={1}>
                {duration} mins
              </Box>
            </>
          </Box>
        </Box>
        <MeetingScheduleButton component={component} program={program} />
      </Flex>
    </Box>
  );
}

interface MeetingSectionProps {
  components: LeadComponent[];
  showDoneButton?: boolean;
  program: MainProgram;
  refetchProgramComponents: () => void;
}

export function MeetingSection({
  components,
  showDoneButton = false,
  program,
  refetchProgramComponents,
}: MeetingSectionProps) {
  const [mutate, { loading }] = useCompleteAddToCalendarMutation({
    onCompleted: () => {
      refetchProgramComponents();
    },
  });

  const onDismiss = () => {
    mutate({
      variables: {
        programId: program.id,
      },
    });
  };

  if (components.length === 0) {
    return null;
  }

  return (
    <Box
      flex={1}
      px={[16, 24]}
      py={[16, 24]}
      mb={24}
      className={CARD_CLASSES}
      css={{ background: Colors.WHITE }}
      maxWidth={720}
    >
      <Flex pb={0} flexDirection="column" style={{ gap: 16 }}>
        <Flex pb={0} alignItems="center" justifyContent="space-between">
          <Flex alignItems={"center"} flex={1}>
            <EmbedSvg src={calendar} height={48} width={48} />
            <Box ml={12}>
              <Box fontSize={20} fontWeight={600}>
                Add Meetings to Calendar
              </Box>
              <Box color={"#394B59"} fontWeight={400}>
                Send invites for your scheduled meetings
              </Box>
            </Box>
          </Flex>
        </Flex>

        <Callout intent="primary" icon={null}>
          💡 We recommend adding participants before sending calendar invites
        </Callout>
        {components.map((component) => (
          <MeetingSectionItem
            key={component.id}
            component={component}
            program={program}
          />
        ))}
        {showDoneButton && (
          <Box>
            <Button
              large={true}
              intent="primary"
              loading={loading}
              onClick={onDismiss}
              style={{
                padding: "13px 48px",
              }}
            >
              All Done
            </Button>
          </Box>
        )}
      </Flex>
    </Box>
  );
}

interface CompleteSectionProps {
  isLead: boolean;
  isParticipant: boolean;
  leadCompleted: LeadComponent[];
  participantCompleted: ParticipantComponent[];
  program: MainProgram;
  isDesignComplete: boolean;
  isLaunchComplete: boolean;
  handleEditComponent: (component: PanelComponent) => void;
  handleDeleteComponent: (component: PanelComponent) => void;
  handleCloseComponent: (component: PanelComponent) => void;
}

function CompleteSection({
  isLead,
  isParticipant,
  leadCompleted,
  participantCompleted,
  program,
  isDesignComplete,
  isLaunchComplete,
  handleEditComponent,
  handleDeleteComponent,
  handleCloseComponent,
}: CompleteSectionProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const leadCompletedCount = leadCompleted?.length || 0;
  const participantCompletedCount = participantCompleted?.length || 0;

  const anyLeadComplete =
    isLead && (leadCompletedCount > 0 || isDesignComplete || isLaunchComplete);
  const anyParticipantComplete = isParticipant && participantCompletedCount > 0;

  if (!anyLeadComplete && !anyParticipantComplete) {
    return null;
  }

  const totalCompleted = max([leadCompletedCount, participantCompletedCount]);
  const isBothRoles = isLead && isParticipant;

  return (
    <>
      <Divider />
      <TitleBox
        title="Complete"
        componentCount={totalCompleted}
        collapsible={true}
        isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(!isCollapsed)}
      />
      {!isCollapsed && (
        <>
          {anyLeadComplete && (
            <ComponentList
              components={leadCompleted}
              program={program}
              role="lead"
              onEdit={handleEditComponent}
              onDelete={handleDeleteComponent}
              onClose={handleCloseComponent}
            />
          )}
          {isBothRoles && anyParticipantComplete && (
            <Box mt={4}>
              <TitleBox title="Complete (As Participant)" />
            </Box>
          )}
          {anyParticipantComplete && (
            <ComponentList
              components={participantCompleted}
              program={program}
              role="participant"
            />
          )}
        </>
      )}
    </>
  );
}

const getGroupMeetingComponentForAdding = (
  components: LeadComponent[],
): LeadComponent[] =>
  components.filter(
    (component) =>
      component?.type === ComponentTypeEnum.GroupMeeting &&
      !component?.isGroupMeetingSetupDone,
  );

export const filterComponentsByStatus = <T extends PanelComponent>(
  components: T[],
  status: ComponentStatus[],
): T[] => components.filter((c) => status.includes(c.status));

// Helper function to sort components by participantSubmitAt in descending order
const sortByParticipantSubmitAtDesc = <
  T extends { participantSubmitAt?: string | null },
>(
  components: T[],
): T[] => {
  return components.sort((a, b) => {
    if (a.participantSubmitAt && b.participantSubmitAt) {
      return compareDesc(
        new Date(a.participantSubmitAt),
        new Date(b.participantSubmitAt),
      );
    }
    if (a.participantSubmitAt) return -1;
    if (b.participantSubmitAt) return 1;
    return 0;
  });
};

interface ProgramDashboardFeedProps {
  program: MainProgram;
  componentsData: ProgramComponentsQuery;
  isParticipant: boolean;
  isLead: boolean;
  refetchProgramComponents: () => Promise<
    ApolloQueryResult<ProgramComponentsQuery>
  >;
  refetchProgram: () => Promise<ApolloQueryResult<ProgramQuery>>;
}

export function ProgramDashboardFeed({
  program,
  componentsData,
  isParticipant,
  isLead,
  refetchProgramComponents,
  refetchProgram,
}: ProgramDashboardFeedProps) {
  const [selectedComponent, setSelectedComponent] =
    useState<ProgramComponentType>();

  const [activeDialog, setActiveDialog] = useState<ComponentDialog>();
  const expandBlock = getExpandBlock(program);

  const { hasParticipants, sendEnrollmentEmailAt, scheduleLaunchEmail } =
    program || {};

  const leadComponents = getSortedComponents({
    components: ensureNonNullArray(
      componentsData?.programComponents?.leadsComponents,
    ),
  });

  const participantsComponents = ensureNonNullArray(
    componentsData?.programComponents?.participantsComponents,
  );

  const participantComponents = getSortedComponents({
    components: participantsComponents.map((component) => ({
      ...component,
      requiredSendLaunchEmail: false,
      forAllParticipants: false,
    })),
    sortByDependency: true,
  });

  const leadOngoing = filterComponentsByStatus(leadComponents, [
    ComponentStatus.Ongoing,
  ]);

  const leadInDesignComponents = filterComponentsByStatus(leadComponents, [
    ComponentStatus.InDesign,
  ]);

  const leadUpcoming = filterComponentsByStatus(leadComponents, [
    ComponentStatus.Upcoming,
    ComponentStatus.Soon,
  ]);

  const leadCompleted = filterComponentsByStatus(leadComponents, [
    ComponentStatus.Completed,
  ]);

  // First filter components by status
  const filteredParticipantOngoing = filterComponentsByStatus(
    participantComponents,
    [
      ComponentStatus.Ongoing,
      ComponentStatus.PastDue,
      ComponentStatus.Started,
      ComponentStatus.NotStarted,
    ],
  );

  // Then sort to move OneOneCoachingIntro to the top
  const sortedParticipantOngoing = filteredParticipantOngoing.sort((a, b) => {
    // First, prioritize OneOneCoachingIntro components
    if (
      a.type === ComponentTypeEnum.OneOneCoachingIntro &&
      b.type !== ComponentTypeEnum.OneOneCoachingIntro
    )
      return -1;
    if (
      b.type === ComponentTypeEnum.OneOneCoachingIntro &&
      a.type !== ComponentTypeEnum.OneOneCoachingIntro
    )
      return 1;

    // If both are OneOneCoachingIntro, sort by targetStartDate
    if (
      a.type === ComponentTypeEnum.OneOneCoachingIntro &&
      b.type === ComponentTypeEnum.OneOneCoachingIntro
    ) {
      // Default to ascending order (earlier dates first)
      if (a.targetStartDate && b.targetStartDate) {
        return (
          new Date(a.targetStartDate).getTime() -
          new Date(b.targetStartDate).getTime()
        );
      }
      // Handle case where one or both might not have dates
      if (a.targetStartDate) return -1;
      if (b.targetStartDate) return 1;
    }

    // For all other components maintain original order
    return 0;
  });

  const [participantOngoing, blockedParticipantOngoing] =
    sortedParticipantOngoing.reduce(
      (
        [ongoing, blocked]: [ParticipantComponent[], ParticipantComponent[]],
        component,
      ) => {
        if (
          component.prerequisiteComponent &&
          !component.prerequisiteComponent.participantSubmitAt
        ) {
          // Blocked components are components that have a prerequisite component and it's not completed
          blocked.push(component);
        } else {
          ongoing.push(component);
        }

        return [ongoing, blocked];
      },
      [[], []],
    );

  const participantUpcoming = [
    // Blocked ongoing components will be moved to the looking forward section
    ...blockedParticipantOngoing,
    ...filterComponentsByStatus(participantComponents, [
      ComponentStatus.Upcoming,
      ComponentStatus.Soon,
    ]),
  ];

  const participantCompleted = sortByParticipantSubmitAtDesc(
    filterComponentsByStatus(participantComponents, [
      ComponentStatus.Completed,
      ComponentStatus.NoResponse,
      ComponentStatus.DidNotFinish,
      ComponentStatus.DidNotPass,
      ComponentStatus.Missed,
    ]),
  );

  const isDesignComplete = program?.hasCompletedSetupComponents ?? false;

  const isLaunchComplete =
    ((hasParticipants && !!sendEnrollmentEmailAt && scheduleLaunchEmail) ||
      (program?.status &&
        [ProgramStatus.Live, ProgramStatus.Complete].includes(
          program.status,
        ))) ??
    false;

  function handleEditComponent(component: ProgramComponentType) {
    setSelectedComponent(component);
    setActiveDialog("add-component");
  }

  function handleDeleteComponent(component: ProgramComponentType) {
    setSelectedComponent(component);
    setActiveDialog("delete-component");
  }

  function handleCloseComponent(component: ProgramComponentType) {
    setSelectedComponent(component);
    setActiveDialog("close-component");
  }

  function handleAddComponent() {
    setActiveDialog("add-component");
  }

  const closeDialog = () => {
    setActiveDialog(undefined);
    setSelectedComponent(undefined);
  };

  const hasTimelineSlipped =
    (program &&
      compareDesc(new Date(program.targetLaunchDate!), new Date()) >= 0) ||
    leadComponents?.some(
      (obj) =>
        isLeadComponent(obj) &&
        obj?.targetStartDate &&
        compareDesc(new Date(obj?.targetStartDate), new Date()) >= 0,
    );

  const designPanel = isLead && program?.hasComponents && (
    <DesignProgramPanel
      data={{
        icon: design,
        title: "Design Your Program",
        description: "Build your program by adding components",
      }}
      program={program}
      components={leadComponents}
      hasTimelineSlipped={hasTimelineSlipped}
      onAddComponent={handleAddComponent}
      onEditComponent={handleEditComponent}
      onDeleteComponent={handleDeleteComponent}
      onCloseComponent={handleCloseComponent}
      refetchProgram={refetchProgram}
      refetchProgramComponents={refetchProgramComponents}
    />
  );

  const launchPanel = isLead && (
    <LaunchProgramPanel
      data={{
        icon: launch,
        title: "Launch Program",
        description: "Let's make the launch a resounding success",
      }}
      program={program}
      expandBlock={expandBlock}
    />
  );

  const anyLeadOngoing =
    isLead &&
    (leadOngoing.length > 0 || !isDesignComplete || !isLaunchComplete);

  const anyParticipantOngoing = isParticipant && participantOngoing.length > 0;

  const isBothRoles = isLead && isParticipant;
  const shouldShowMeetingSection = program?.status === ProgramStatus.Live;
  const shouldShowPanel = anyLeadOngoing || anyParticipantOngoing;

  return (
    <Box>
      {shouldShowPanel ? (
        <>
          {isLead && !isDesignComplete && designPanel}
          {isLead && !isLaunchComplete && launchPanel}
        </>
      ) : (
        <AllSetForNow />
      )}
      {shouldShowMeetingSection && (
        <MeetingSection
          components={getGroupMeetingComponentForAdding(leadUpcoming)}
          showDoneButton={true}
          program={program}
          refetchProgramComponents={refetchProgramComponents}
        />
      )}
      {shouldShowPanel && (
        <>
          {isLead && leadOngoing.length > 0 && (
            <ComponentList
              components={leadOngoing}
              program={program}
              role="lead"
              onEdit={handleEditComponent}
              onDelete={handleDeleteComponent}
              onClose={handleCloseComponent}
            />
          )}
          {isBothRoles && anyParticipantOngoing && (
            <Box mt={4}>
              <TitleBox title="Ongoing (As Participant)" />
            </Box>
          )}
          {isParticipant && participantOngoing.length > 0 && (
            <ComponentList
              components={participantOngoing}
              program={program}
              role="participant"
            />
          )}
        </>
      )}

      {isLead && leadInDesignComponents.length > 0 && (
        <>
          <Divider />
          <ComponentList
            role="lead"
            title="In Design"
            components={leadInDesignComponents}
            program={program}
            onEdit={handleEditComponent}
            onDelete={handleDeleteComponent}
            onClose={handleCloseComponent}
          />
        </>
      )}

      <LookingForwardSection
        isLead={isLead}
        isParticipant={isParticipant}
        isBothRoles={isLead && isParticipant}
        leadUpcoming={leadUpcoming}
        participantUpcoming={participantUpcoming}
        program={program}
        handleEditComponent={handleEditComponent}
        handleDeleteComponent={handleDeleteComponent}
        handleCloseComponent={handleCloseComponent}
        refetchProgramComponents={refetchProgramComponents}
      />

      <CompleteSection
        isLead={isLead}
        isParticipant={isParticipant}
        leadCompleted={leadCompleted}
        participantCompleted={participantCompleted}
        program={program}
        isDesignComplete={isDesignComplete}
        isLaunchComplete={isLaunchComplete}
        handleEditComponent={handleEditComponent}
        handleDeleteComponent={handleDeleteComponent}
        handleCloseComponent={handleCloseComponent}
      />

      {isLead && (
        <ProgramDashboardDialogs
          program={program}
          selectedComponent={selectedComponent}
          dialog={activeDialog}
          onCloseDialog={closeDialog}
        />
      )}
    </Box>
  );
}
